<template>
    <div id="profile" class="pb-5">
        <div id="profile-hero">
            <div id="profile-banner" :style="{'background-image': `url(${contributor.profile_banner_url})`}"/>
            <div class="container">
                <div id="profile-image">
                    <img :src="contributor.profile_image_url"/>
                    <div id="profile-counts">
                        <span>{{ contributor.albums_count }} Albums</span>
                        <span>{{ contributor.images_count }} Images</span>
                    </div>
                </div>
                <div id="profile-details">
                    <h1>{{ contributor.name }}</h1>
                    <div id="profile-location">{{ contributor.location }}</div>
                    <div v-if="contributor.website || contributor.instagram" id="profile-links">
                        <a v-if="contributor.website" :href="`//${contributor.website}`" target="_blank">{{ contributor.website }}</a>
                        <div v-if="contributor.website && contributor.instagram" class="divider">|</div>
                        <a v-if="contributor.instagram" :href="`https://www.instagram.com/${contributor.instagram.replace('@', '')}`" target="_blank">{{ contributor.instagram }}</a>
                    </div>
                    <div id="profile-counts-mobile">
                        <span>{{ contributor.albums_count }} Albums</span>&nbsp;&nbsp;|&nbsp;&nbsp;<span>{{ contributor.images_count }} Images</span>
                    </div>
                    <div id="profile-about">
                        <template v-if="contributor.about">{{ contributor.about }}</template>
                        <template v-else>
                            <template v-if="locale === 'nz'">
                                I'm a local New Zealand photographer and an active contributor with Truestock. Truestock is a locally owned
                                <nobr>co-operative</nobr>
                                making all its contributors
                                <nobr>co-owners</nobr>
                                as well.
                                We're here to champion the representation of a truly diverse and beautiful Aotearoa, through our stock imagery.
                            </template>
                            <template v-else-if="locale === 'aus'">
                                I'm a local Australian photographer and an active contributor with Truestock.
                                We're here to champion the representation of a truly diverse and beautiful Australia, through our stock imagery and videos.
                            </template>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="albums && albums.length > 0" class="text-center">
            <h2 class="heading clickable" :class="{ active: viewMode === 'albums'}" @click="viewMode = 'albums'">Albums</h2>
            <h2 class="heading clickable" :class="{ active: viewMode === 'images'}" @click="viewMode = 'images'">Images</h2>
        </div>
        <div v-if="viewMode === 'albums'">
            <loading :loading="albumsLoading" :full-page="false" class="mt-5"/>
            <div v-if="albums && albums.length > 0" id="curated-albums-area">
                <div class="container">
                    <div class="row additional-images-row">
                        <album v-for="album in albums" :key="album.id" :album="album" :slug="contributor.slug"/>
                    </div>
                </div>
            </div>
            <div v-if="featuredImages && featuredImages.length > 0" class="container">
                <h2 class="heading">Featured Images</h2>
                <div class="featured-images-row search-results-container">
                    <div class="similar-images-wrap">
                        <stock-image-thumbnail v-for="featuredImage in featuredImages" :key="featuredImage.id" :image="featuredImage" :show-name-link="false"/>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="viewMode === 'images'" class="container">
            <div class="row">
                <div class="col-12">
                    <div class="welcome search-results-container">
                        <div class="similar-images-wrap">
                            <loading :loading="imagesLoading" :full-page="false"/>
                            <stock-image-thumbnail v-for="image in allImages" :key="image.id" :image="image" :show-name-link="false"/>
                        </div>
                        <v-button v-if="!noMoreImages" :loading="loadingImages" class="btn-default btn-hollow w-auto pt-3 pb-3 mt-4 pl-5 pr-5 d-block ml-auto mr-auto h6" @click.native="getAllImages">
                            See More {{ locale === 'nz' ? 'Images / Videos' : 'Images' }}
                        </v-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import Album from '~/components/contributor/Album'
import StockImageThumbnail from '~/components/StockImageThumbnail'
import Loading from '~/components/global/Loading'

export default {
    components: {
        Loading,
        StockImageThumbnail,
        Album
    },
    async asyncData ({
        params
    }) {
        const contributor = await axios.get(`/contributor/${params.slug}`).then(rsp => rsp.data.data)

        return {
            contributor
        }
    },
    data () {
        return {
            contributor: null,
            albums: null,
            featuredImages: null,
            allImages: [],
            noMoreImages: false,
            loadingImages: false,
            imagesPerPage: 40,
            viewMode: 'albums',
            page: 1,
            randomSeed: Math.floor(Math.random() * 1000),
            albumsLoading: true,
            imagesLoading: true
        }
    },
    head () {
        const title = `${this.contributor.name} on Truestock`
        const description = this.contributor.description

        return {
            title,
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: description
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: description
                },
                {
                    hid: 'twitter:description',
                    name: 'twitter:description',
                    content: description
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: title
                },
                {
                    hid: 'twitter:title',
                    name: 'twitter:title',
                    content: title
                },
                {
                    hid: 'og:image',
                    property: 'og:image',
                    content: this.contributor.profile_image_url
                },
                {
                    hid: 'twitter:image',
                    name: 'twitter:image',
                    content: this.contributor.profile_image_url
                }
            ]
        }
    },
    mounted () {
        this.fetchAlbums()
        this.fetchFeaturedImages()

        this.getAllImages()
    },
    methods: {
        async fetchAlbums () {
            await axios.get(`/contributor/${this.contributor.slug}/albums`)
                .then(rsp => rsp.data)
                .then((response) => {
                    if (response.data.length > 0) {
                        this.albums = response.data
                    }
                })
                .finally(() => {
                    this.albumsLoading = false
                })
        },
        async fetchFeaturedImages () {
            await axios.get(`/contributor/${this.contributor.slug}/featured-images`)
                .then(rsp => rsp.data)
                .then((response) => {
                    if (response.data.length > 0) {
                        this.featuredImages = response.data
                    }
                })
        },
        getAllImages () {
            this.loadingImages = true

            axios({
                method: 'get',
                url: `/contributor/${this.contributor.slug}/images`,
                params: {
                    page: this.page,
                    randomSeed: this.randomSeed,
                    perPage: this.imagesPerPage
                }
            }).then(rsp => rsp.data)
                .then((rsp) => {
                    if (rsp.data.length) {
                        this.page += 1
                        this.allImages.push(...rsp.data)

                        if (rsp.data.length < this.imagesPerPage) {
                            this.noMoreImages = true
                        }
                    } else {
                        this.noMoreImages = true
                    }
                })
                .finally(() => {
                    this.imagesLoading = false
                    this.loadingImages = false
                })
        }
    }
}
</script>
